import { Fragment, useCallback, useEffect, useState } from "react";
import { AlertTriangle, History, Info, ListTree } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { StatusBadge } from "./StatusBadge";
import { WorkflowActions } from "./WorkflowActions";
import { DrillDialog } from "./DrillDialog";
import { NoVisitFollowUps } from "./NoVisitFollowUps";
import { errMsg, fmtKpi, toneCls, weekLabel } from "./kpiFormat";
import { DetailServiceRows, ExpandToggle, byService } from "./ServiceBreakdown";
import { CampaignCostLine, LeadSourcesLine } from "./LeadSources";
import { FormConversion } from "./FormConversion";
import { DataChecks } from "./DataChecks";

const COMPONENT_LABELS = {
  cash_receipts: "cash receipts", transactions: "transactions", refunds: "refunds", recurring: "recurring",
  renewal_due: "renewal due (grace)", intro: "intro", pack: "pack", this_week_leads_converted_so_far: "this week's leads bought so far",
  this_week_leads: "this week's leads", eligible: "eligible", converted: "converted",
  pending_deadline_after_cutoff: "deadline still open", active_at_month_start: "active at month start",
  acquired: "acquired", reactivated: "reactivated", churned: "churned", product_sales: "product sales",
};

const detailLine = (r, unit) => {
  const bits = [];
  if (r.numerator !== null && r.numerator !== undefined && r.denominator !== null && r.denominator !== undefined) {
    bits.push(`${fmtKpi(r.numerator, unit === "pct" ? "count" : unit === "ratio" ? "count" : unit)} ÷ ${r.denominator}`);
  }
  Object.entries(r.components || {}).forEach(([k, v]) => {
    if (typeof v !== "object") bits.push(`${COMPONENT_LABELS[k] || k}: ${k === "product_sales" || k === "cash_receipts" ? fmtKpi(v, "money2") : v}`);
  });
  return bits.join(" · ");
};

const ProductSalesLine = ({ r, onDrill }) => {
  const c = r?.components || {};
  return (
    <tr className="bg-zinc-50/60 align-top" data-testid="kpi-detail-row-product-sales">
      <td className="py-2 pr-3 pl-4 text-xs text-zinc-500">
        {c.product_sales === undefined ? "↳ of which product sales" : <DrillLink id="product_sales" onDrill={onDrill}>↳ of which product sales</DrillLink>}
      </td>
      <td className="py-2 pr-3 font-mono-num text-xs text-zinc-700 whitespace-nowrap" data-testid="kpi-detail-product-sales-value">
        {c.product_sales === undefined ? "—" : fmtKpi(c.product_sales, "money2")}
      </td>
      <td className="py-2 pr-3 text-xs text-zinc-400">—</td>
      <td className="py-2 pr-3 text-xs text-zinc-500" colSpan={2} data-testid="kpi-detail-product-sales-breakdown">
        {c.product_sales === undefined
          ? "Not in this snapshot — shown from the next draft onward"
          : (c.product_sales_by_category || []).map((x) => `${x.name} ${fmtKpi(x.net, "money2")}`).join(" · ") ||
            "No sales of products mapped to a product category in these 30 days"}
        {c.product_sales !== undefined && <span className="text-zinc-400"> · ex GST, same 30 days, already inside revenue</span>}
      </td>
    </tr>
  );
};

const DrillLink = ({ id, onDrill, children, className }) => (
  <button type="button" onClick={() => onDrill(id)} data-testid={`kpi-drill-open-${id}`}
    className={cn("group inline-flex items-center gap-1.5 text-left hover:text-blue-700", className)}
    title="See who and what is counted in this figure">
    <span className="underline decoration-zinc-300 decoration-dotted underline-offset-4 group-hover:decoration-blue-600">{children}</span>
    <ListTree className="h-3.5 w-3.5 text-zinc-300 transition-colors group-hover:text-blue-600" />
  </button>
);

const KpiRow = ({ def, r, cmp, note, editable, onNote, onDrill, open, onToggle }) => (
  <tr className="border-t border-zinc-100 align-top" data-testid={`kpi-detail-row-${def.id}`}>
    <td className="py-2.5 pr-3 text-zinc-700">
      {onToggle && <ExpandToggle open={open} onToggle={onToggle} testId={`kpi-detail-expand-${def.id}`} />}
      {r ? <DrillLink id={def.id} onDrill={onDrill}>{def.label}</DrillLink> : def.label}
    </td>
    <td className={cn("py-2.5 pr-3 font-mono-num whitespace-nowrap", toneCls(r?.status))}>
      {r?.value === null || r?.value === undefined ? <span className="text-zinc-400 italic">N/A</span> : fmtKpi(r.value, def.unit)}
      {r?.provisional && <span className="ml-1.5 text-2xs uppercase tracking-wider text-amber-700">prov.</span>}
    </td>
    <td className="py-2.5 pr-3 font-mono-num text-zinc-500 whitespace-nowrap">
      {cmp?.previous === null || cmp?.previous === undefined ? "—" : fmtKpi(cmp.previous, def.unit)}
      {cmp?.delta ? <span className={cn("ml-1", (cmp.delta > 0) === (def.better !== "lower") ? "text-emerald-700" : "text-red-600")}>
        ({cmp.delta > 0 ? "+" : ""}{Number(cmp.delta).toLocaleString("en-AU")})</span> : null}
    </td>
    <td className="py-2.5 pr-3 text-xs text-zinc-500">{r?.reason || (r ? detailLine(r, def.unit) : "")}</td>
    <td className="py-2 w-56">
      {editable ? (
        <Input value={note || ""} onChange={(e) => onNote(def.id, e.target.value)} className="h-8 text-xs"
          placeholder="Note" data-testid={`kpi-note-${def.id}`} />
      ) : <span className="text-xs text-zinc-600">{note}</span>}
    </td>
  </tr>
);

const HistoryList = ({ report }) => (
  <div className="space-y-1.5 text-xs text-zinc-600" data-testid="kpi-history">
    {[...(report.history || [])].reverse().map((h, i) => (
      <div key={i} className="flex gap-2">
        <span className="font-mono-num text-zinc-400 shrink-0">{fmtDateTime(h.at)}</span>
        <span><b className="text-zinc-800">{h.action === "week_format_changed" ? "moved to Saturday–Friday" : h.action}</b> by {h.by}{h.auto ? " (automatic)" : ""}{h.note ? ` — ${h.note}` : ""}</span>
      </div>
    ))}
    {(report.versions || []).map((v) => (
      <div key={v.version} className="flex gap-2 text-zinc-500" data-testid={`kpi-version-${v.version}`}>
        <History className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        Version {v.version} ({v.status}, approved by {v.approved_by || "—"}) superseded {fmtDateTime(v.superseded_at)} by {v.superseded_by}: {v.superseded_reason}
      </div>
    ))}
  </div>
);

export const WeekDetail = ({ week, kpis, canApprove, onChanged }) => {
  const [rep, setRep] = useState(null);
  const [notes, setNotes] = useState("");
  const [kpiNotes, setKpiNotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [drill, setDrill] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const closeDrill = useCallback(() => setDrill(null), []);

  const apply = (d) => { setRep(d); setNotes(d.notes || ""); setKpiNotes(d.kpi_notes || {}); };
  useEffect(() => {
    setRep(null);
    api.get(`/kpi/reports/${week}`).then((r) => apply(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [week]);

  if (!rep) return <div className="h-40 animate-pulse rounded-lg bg-zinc-100" data-testid="kpi-detail-loading" />;
  const editable = ["draft", "review"].includes(rep.status);
  const changed = (d) => { apply(d); onChanged?.(); };
  const refresh = async () => {
    changed((await api.post(`/kpi/reports/${week}/generate`, null, { timeout: 120000 })).data);
    toast.success("Draft refreshed");
  };
  const saveNotes = async () => {
    setSaving(true);
    try {
      changed((await api.put(`/kpi/reports/${week}/notes`, { notes, kpi_notes: kpiNotes })).data);
      toast.success("Notes saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6 space-y-5" data-testid="kpi-week-detail">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Reporting week (Sat – Fri)</div>
          <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight" data-testid="kpi-detail-title">{weekLabel(rep, true)}</h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
            <StatusBadge status={rep.status} testId="kpi-detail-status" />
            {rep.version && <span>Version {rep.version}</span>}
            {rep.generated_at && <span>· calculated {fmtDateTime(rep.generated_at)} by {rep.generated_by}</span>}
            {rep.approved_by && <span>· approved by {rep.approved_by}</span>}
            {rep.config_version && <span className="font-mono-num">· config {rep.config_version}</span>}
          </div>
        </div>
        <WorkflowActions report={rep} canApprove={canApprove} onChange={changed} />
      </div>
      {rep.restates_version && (
        <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid="kpi-restatement-banner">
          <Info className="h-4 w-4 mt-0.5" />Restatement of version {rep.restates_version}: {rep.correction_reason}
        </div>
      )}
      {rep.status === "none" ? (
        <p className="text-sm text-zinc-500" data-testid="kpi-detail-empty">
          {rep.finished ? "No draft yet for this week. Generate one to calculate the figures." : "This week is still running — the draft is built automatically on Saturday morning."}
        </p>
      ) : (
        <>
          <DataChecks items={rep.warnings} groups={rep.warning_items} canAssign={canApprove}
            onRefresh={rep.status === "draft" ? refresh : null} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-2xs uppercase tracking-[0.18em] text-zinc-500">
                <th className="pb-2">KPI</th><th className="pb-2">This week</th><th className="pb-2">Previous</th>
                <th className="pb-2">How it was calculated</th><th className="pb-2">Note</th></tr></thead>
              <tbody>
                {kpis.filter((k) => k.id).map((d) => (
                  <Fragment key={d.id}>
                    <KpiRow def={d} r={rep.results?.[d.id]} cmp={rep.compare?.[d.id]} note={kpiNotes[d.id]} onDrill={setDrill}
                      editable={editable} onNote={(id, v) => setKpiNotes((p) => ({ ...p, [id]: v }))}
                      open={open.has(d.id)} onToggle={byService(rep.results?.[d.id]) ? () => toggle(d.id) : null} />
                    {open.has(d.id) && <DetailServiceRows def={d} r={rep.results?.[d.id]} cmp={rep.compare?.[d.id]}
                      onDrill={(service) => setDrill({ id: d.id, service })} />}
                    {d.id === "rolling_30d_revenue" && rep.results?.[d.id]?.value != null &&
                      <ProductSalesLine r={rep.results[d.id]} onDrill={setDrill} />}
                    {d.id === "meta_paid_leads" && rep.results?.[d.id]?.value != null &&
                      <LeadSourcesLine r={rep.results[d.id]} onOpen={(search) => setDrill({ id: d.id, search })} />}
                    {d.id === "cost_per_lead" && rep.results?.fb_spend?.value != null &&
                      <CampaignCostLine r={rep.results[d.id]} />}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Facebook form conversion</div>
            <FormConversion week={week} />
          </div>
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Member follow-up</div>
            <NoVisitFollowUps week={week} onOpenList={() => setDrill("no_visit_members")} />
          </div>
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Manager notes</div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} disabled={!editable}
              placeholder="What happened this week?" data-testid="kpi-notes-input" />
            {editable && <Button size="sm" variant="outline" onClick={saveNotes} disabled={saving} data-testid="kpi-notes-save-btn">Save notes</Button>}
          </div>
          <HistoryList report={rep} />
          {drill && (
            <DrillDialog week={week} onClose={closeDrill}
              onChanged={() => { api.get(`/kpi/reports/${week}`).then((r) => apply(r.data)).catch(() => {}); onChanged?.(); }}
              kpi={drill === "product_sales" ? { id: "product_sales", label: "Product sales (retail)", unit: "money2" }
                : drill === "no_visit_members" ? { id: "no_visit_members", label: "Members with no visits this week", unit: "count" }
                : drill.service ? { ...kpis.find((k) => k.id === drill.id), service: drill.service }
                : drill.id ? { ...kpis.find((k) => k.id === drill.id), search: drill.search }
                : kpis.find((k) => k.id === drill)} />
          )}
        </>
      )}
    </section>
  );
};
